<template>
  <div class="min-h-screen bg-blog-beige/30 py-12 px-4 sm:px-6">
    <!-- Container do Recibo -->
    <div 
      ref="invoiceContainer" 
      class="max-w-3xl mx-auto bg-[#fff8f1] rounded-2xl border border-blog-gold shadow-xl p-8 md:p-12 text-left relative overflow-hidden"
    >
      <div v-if="payment" class="text-secondary relative z-10">
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start pb-8 border-b border-blog-gold">
          <div>
            <img src="@/assets/mp-logo.webp" class="w-36 pt-2 mb-4 object-contain" alt="Mercado Pago">
            <h1 class="f-font-times-new-roman text-3xl font-bold text-blog-old-red tracking-tight">
              {{ translate('payment receipt', 'capitalize') }}
            </h1>
            <p class="text-xs text-gray-500 mt-1 font-light">
              {{ DateHandler.format(payment.raw_date, 'DATE_FULL') }}
            </p>
          </div>
          
          <div class="md:text-right space-y-3 mt-2 md:mt-0">
            <div class="flex md:justify-end">
              <span 
                class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border shadow-sm transition-all"
                :class="[statusBadgeConfig.classes, { 'print-regulation-area': exporting }]"
              >
                <i :class="statusBadgeConfig.icon" class="text-[10px]"></i>
                {{ statusBadgeConfig.label }}
              </span>
            </div>

            <p class="text-xs text-gray-600">
              <span class="mr-1 text-gray-400 uppercase tracking-wider text-[10px] font-semibold">
                {{ translate('transaction number', 'ucfirst') }}:
              </span>
              <span 
                class="font-mono font-bold text-blog-old-red text-sm" 
                :class="{ 'print-regulation-area': exporting }"
              >
                {{ payment.payment_id }}
              </span>
            </p>

            <p class="text-xs text-gray-600">
              <strong class="font-normal text-gray-400 uppercase tracking-wider text-[10px]">{{ translate('emission', 'ucfirst') }}:</strong> 
              <span class="font-medium text-gray-700 ml-1">{{ DateHandler.format(payment.raw_date, 'DATE_SHORT') }}</span>
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 my-8 text-sm">
          <div class="md:border-r md:border-blog-gold pr-4">
            <h3 class="text-[10px] font-bold text-blog-red uppercase tracking-widest mb-1.5">
              {{ translate('provider', 'ucfirst') }}
            </h3>
            <p class="f-font-times-new-roman text-xl font-bold text-blog-old-red">
              {{ context.companyNickname }}
            </p>
            <p class="text-gray-500 italic text-xs mt-0.5">
              {{ context.companyName }}
            </p>
          </div>

          <div>
            <h3 class="text-[10px] font-bold text-blog-red uppercase tracking-widest mb-1.5">
              {{ translate('buyer', 'ucfirst') }}
            </h3>
            <p class="f-font-times-new-roman text-xl font-bold text-blog-old-red">
              {{ payment.payer_name || translate('student', 'ucfirst') }}
            </p>
          </div>
        </div>

        <div class="my-8">
          <p class="pt-4 pb-3 text-xs font-bold border-t border-blog-gold/30 text-blog-red uppercase tracking-widest">
            {{ translate('purchase information', 'ucfirst') }}
          </p>
          
          <div class="space-y-2 bg-white/60 p-4 rounded-xl border border-blog-gold">
            <div 
              v-for="(item, index) of payment.items" 
              :key="index" 
              class="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-none"
            >
              <div class="flex items-center">
                <i class="pi pi-sparkles text-blog-red text-xs mr-2 opacity-70"></i>
                <p class="font-medium text-gray-800">{{ item.title }}</p>
              </div>
              <p class="font-mono text-gray-700 font-semibold">
                R$ {{ item.unit_price?.toFixed(2) }}
              </p>
            </div>
          </div>
        </div>

        <div class="mt-8 border-t border-blog-gold pt-4 flex flex-col justify-end text-right">
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-0.5">{{ translate('total', 'ucfirst') }}:</p>
          <p class="f-font-times-new-roman font-bold text-3xl md:text-4xl text-blog-old-red font-mono">
            {{ payment.total }}
          </p>
        </div>
        
        <div class="mt-12 pt-6 border-t border-blog-gold">
          <p class="text-xs text-center text-gray-400 leading-relaxed font-light">
            {{ translate('this is an invoice generated automatically by the system', 'ucfirst') }} {{ context.companyNickname }}.<br>
            {{ translate('Questions? Contact us.') }} 
            <a :href="`mailto:${context.supportEmail}`" class="text-carrot font-medium underline hover:opacity-80 transition">
              {{ context.supportEmail }}
            </a>
          </p>
        </div>
      </div>
    </div>
    
    <div v-if="!loading" class="max-w-3xl mx-auto mt-6 flex flex-wrap justify-center md:justify-end gap-3">
      <Button 
        :label="translate('export to pdf', 'ucfirst')" 
        icon="pi pi-file-pdf" 
        @click="exportToPdf()" 
        :loading="exporting"
        class="bg-button bg-blog-red hover:bg-blog-old-red text-white px-6 py-2.5 text-sm font-semibold rounded-full shadow-md transition border-none cursor-pointer w-full md:w-auto" 
      />
      <Button 
        :label="translate('see item', 'ucfirst')" 
        icon="pi pi-angle-right" 
        @click="goToItemPage()" 
        class="no-bg-button bg-blog-red hover:bg-blog-old-red text-white px-6 py-2.5 text-sm font-semibold rounded-full shadow-md transition border-none cursor-pointer w-full md:w-auto" 
      />
    </div>

    <LoadingDialog :visible="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import DateHandler from "@/composables/handlers/DateHandler";
import { translate } from '@/composables/translate';
import { useRoute } from 'vue-router';
import PaymentService from '@/services/PaymentService';
import urlHandler from '@/composables/urlHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import Button from 'primevue/button';
import html2pdf from 'html2pdf.js';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue';

const { useSessionMessageStore } = require('@/stores/sessionMessage');
const { useContextStore } = require('@/stores/context');

const toast = useToast();
const sessionMessageStore = useSessionMessageStore();
const context = useContextStore();
const route = useRoute();

const payment = ref(null);
const loading = ref(true);
const exporting = ref(false);
const invoiceContainer = ref(null);

/**
 * Mapeamento e Computação visual do Status de Pagamento
 */
const statusBadgeConfig = computed(() => {
  const status = (payment.value?.status || '').toLowerCase();

  switch (status) {
    case 'approved':
      return {
        label: translate('paid', 'ucfirst'),
        classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        icon: 'pi pi-check-circle'
      };
    case 'rejected':
    case 'refused':
    case 'cancelled':
      return {
        label: translate('rejected', 'ucfirst'),
        classes: 'bg-rose-50 text-rose-700 border-rose-200',
        icon: 'pi pi-times-circle'
      };
    case 'pending':
    case 'in_process':
      return {
        label: translate('pending', 'ucfirst'),
        classes: 'bg-amber-50 text-amber-700 border-amber-200',
        icon: 'pi pi-clock'
      };
    case 'refunded':
    case 'charged_back':
      return {
        label: translate('refunded', 'ucfirst'),
        classes: 'bg-sky-50 text-sky-700 border-sky-200',
        icon: 'pi pi-refresh'
      };
    default:
      return {
        label: translate('not paid', 'ucfirst'),
        classes: 'bg-gray-100 text-gray-700 border-gray-300',
        icon: 'pi pi-exclamation-circle'
      };
  }
});

onMounted(() => {
  getPayment();
});

async function getPayment() {
  const id = route.params?.id;

  if (!id) {
    redirectToDashboard();
    return;
  }

  loading.value = true;
  const response = await PaymentService.getPayment(id);
  loading.value = false;

  if (!response) {
    redirectToDashboard();
    return;
  }

  payment.value = response.simplifyData();
}

function redirectToDashboard() {
  sessionMessageStore.errorMessage(translate('payment not found'));
  urlHandler.redirectAndScroll('/dashboard');
}

async function exportToPdf() {
  if (!payment.value) return;

  exporting.value = true;
  await nextTick();
  
  const options = {
    margin: [10, 10, 10, 10], 
    filename: `${translate('receipt', 'ucfirst')}_${payment.value.payment_id}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2, 
      useCORS: true, 
      logging: false 
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait' 
    }
  };

  html2pdf()
    .set(options)
    .from(invoiceContainer.value)
    .save()
    .then(() => {
      exporting.value = false;
    })
    .catch(() => {
      exporting.value = false;
    });
}

function goToCoursePage(courseUuid = null) {
    if (courseUuid) {
        urlHandler.redirectAndScroll(`/course/${courseUuid}`);
    } else {
        showToast(toast, translate('course not found', 'ucfirst'), 'info');
    }
}

function goToSellingMaterialGroupPage(sellingMaterialGroupId = null) {
    if (sellingMaterialGroupId) {
        urlHandler.redirectAndScroll(`/dashboard/teacher_materials`, {selected: sellingMaterialGroupId});
    } else {
        showToast(toast, translate('material not found', 'ucfirst'), 'info');
    }
}

function goToItemPage() {
  const item = payment.value.items[0] || null;
  switch (payment.value.product_type) {
      case 'course':
        goToCoursePage(item.uuid);
      break;
      case 'sellingmaterialgroup':
        goToSellingMaterialGroupPage(item.id);
      break;
  }
}

</script>

<style scoped>
.print-regulation-area {
  padding-bottom: 12px !important;
}
</style>